// Third party
import React, { CSSProperties, Component } from 'react';
import camelCase from 'lodash/camelCase';
import classNames from 'classnames';
import DOMPurify from 'dompurify';
import Dotdotdot from 'react-dotdotdot';
import ReactDOM from 'react-dom';
import { Sticky, StickyContainer } from 'react-sticky';

// Components
import ComparisonBaseCell from './ComparisonBaseCell';
import ComparisonBenchmark from '@/views/components/edit-benchmark/ComparisonBenchmark';
import ComparisonCardContacts from './ComparisonCardContacts';
import ComparisonCardHoldingIcon from './ComparisonCardHoldingIcon';
import ComparisonCardMenu from './ComparisonCardMenu';
import ComparisonCardWarnings from './ComparisonCardWarnings';
import ComparisonGroupsMenu from './ComparisonGroupsMenu';
import ComparisonItemGroups from './ComparisonItemGroups';
import ComparisonRiskProfileLabel from '../misc/ComparisonRiskProfileLabel';
import ComparisonValueCell from './ComparisonValueCell';
import CustomTypePills from '@/views/components/flaggableInstruments/CustomTypePills';
import ShelfStatus from '@/views/components/shelf-management/ShelfStatus';
import { CommentExpanded } from '@/views/components/markup-tools';
import { CurrencyLabel, PillLabel, PortfolioTypeLabel } from '@/views/components/misc';
import { DisclaimerTooltip, Tooltip } from '@/views/components/tooltips';
import { GenericTextModal } from '@/views/components/modals';
import { HoldingIcon } from '@/views/components/common';
import { IconButton, PrimaryLink } from '@/views/components/buttons';
import { SelectInput, SelectOption } from '@/views/components/inputs';

// Enums
import { AccountSetRowKind } from '@/enums/AccountSets';

// Hooks
import useThemeVersion from '@/hooks/useThemeVersion';

// Types
import BasicUserInfos from '@/types/basic-user-infos';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import Comparison from '@/types/comparison';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import ComparisonHolding from '@/types/comparison-holding';
import ComparisonItem from '@/types/comparison-item';
import ComparisonItemCategory from '@/types/comparison-item-category';
import { ComparisonItemCategory as ComparisonItemCategoryEnum } from '@/redux/modules/types/comparison';
import ComparisonItemGroup from '@/types/comparison-item-group';
import Locale from '@/types/locale';
import Nullable from '@/types/nullable';
import Portfolio from '@/types/portfolio';
import RiskPeriods from '@/types/risk-periods';
import SelectedCategories from '@/types/selected-categories';
import User from '@/types/user';
import { AltClass, AltClassesTruncated } from '@/types/models/alt';

// Utils
import { comparisonCategoryIsShown, isBenchmark, shouldExpandPortfolioInReadOnly, VALID_GROUP_PRODUCTS } from '@/utils/comparison';
import { COMPARISON_CATEGORIES, COMPARISON_ITEM_TYPES } from '@/utils/constants';
import { CURRENCY } from '@/utils/currency';
import { DISCLAIMER_KEYS, DISCLAIMER_TAG } from '@/utils/disclaimers/constants';
import { formatMessage, formatLocalizedString, FormattedMessage } from '@/utils/i18n';
import { getCustomDisclaimersForItem, getShortHistoryDatesForItem } from '@/utils/disclaimers/comparison';
import { getUniqueFieldId } from '@/utils/markup-tools';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { timeSince } from '@/utils/time';
import { getAccountSetMembershipForComparisonItem } from '@/utils/comparison/account-sets';

// Legacy support
import LegacyComparisonCardContent from './LegacyComparisonCardContent';
import ComparisonCategoryLabel from '../misc/ComparisonCategoryLabel';
import { getShelfNameFromComparisonItem } from '@/utils/user-shelf';

type Props = {
  item: ComparisonItem;
  isFXSubItem: boolean;
  isExpandSubItem: boolean;
  sharingInfo?: Nullable<{ isMine: boolean; label: string }>;
  readOnly?: boolean;
  user?: User | null;
  comparison: Comparison;
  disableSaving?: boolean;
  editedBenchmark?: BenchmarkPortfolio;
  editedPortfolio?: Portfolio;
  onItemRemove: (item: ComparisonItem) => void;
  onModifyPortfolio: (item: ComparisonItem) => void;
  onViewPortfolio: (item: ComparisonItem) => void;
  onClonePortfolio: (item: ComparisonItem) => void;
  addCategoryBenchmark: (item: ComparisonItem) => void;
  onCloneBenchmark: (item: ComparisonItem) => void;
  onSavePortfolio: (item: ComparisonItem, toContact?: boolean) => void;
  onModifyBenchmark: (item: ComparisonItem) => void;
  categories: ComparisonCategory[];
  markupMode?: boolean;
  markupTool?: string;
  onCellClick: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField) => void;
  viewMarkup: boolean;
  previewMarkup?: boolean;
  selectedCategories?: SelectedCategories;
  setMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, comment: string) => void;
  postMarkupComment: (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, dotNumber: number, comment: string) => void;
  getNewDotNumber: () => number;
  clearMarkup: (item: ComparisonItem, categoryKey: string, fieldKey: string, markupTool: string) => void;
  updateTableHeight: (height: number) => void;
  resizeCommentLinesContainer: () => void;
  riskPeriod: RiskPeriods;
  onShowCorrelation: (comparisonItem: ComparisonItem) => void;
  onExpandPortfolioItem: (item: ComparisonItem, expand: boolean) => void;
  onToggleFXItem?: (item: ComparisonItem, toggled: boolean) => void;
  showMenu: boolean;
  menuIsDisabled?: boolean;
  allowExpandPortfolio?: boolean;
  getAdditionalCategoryStyles: (category: ComparisonCategory) => CSSProperties;
  isEdited: boolean;
  disableStickyHeader?: boolean;
  updateComparisonItemHolding: (item: ComparisonItem, holdingId: string, holdingData: Partial<ComparisonHolding>) => void;
  updateComparisonItemColor: (item: ComparisonItem, color: string) => void;
  popoverContainerRef: Nullable<HTMLDivElement>;
  scrollbarContainerRef?: HTMLElement;
  isSelected: boolean;
  hasStickyHeader?: boolean;
  onAddItemToGroup?: (item: ComparisonItem, group: ComparisonItemGroup, value: number, fee?: number) => void;
  onRemoveItemFromGroup?: (item: ComparisonItem, group: ComparisonItemGroup) => void;
  onModifyGroup: (item: ComparisonItem) => void;
  showGroups?: boolean;
  editGroups?: boolean;
  locale?: Locale;
  updateComparisonItemCategory?: (item: ComparisonItem, category: ComparisonItemCategory | undefined) => void;
};

type State = {
  showCustomDisclaimerModal: boolean;
};

class ComparisonCardContentV2 extends Component<Props, State> {
  // These should be in order of priority -- first disclaimer that exists on the item will be displayed.
  HEADER_DISCLAIMERS = {
    [COMPARISON_ITEM_TYPES.FUND]: [
      DISCLAIMER_KEYS.FUND_NO_DATA,
      DISCLAIMER_KEYS.FUND_CLOSED,
      DISCLAIMER_KEYS.FUND_LESS_THAN_3Y,
      DISCLAIMER_KEYS.FUND_LESS_THAN_1Y,
      DISCLAIMER_KEYS.FUND_MISSING_BENCHMARK,
      DISCLAIMER_KEYS.FUND_HAS_SAME_BENCHMARK,
    ],
    [COMPARISON_ITEM_TYPES.STOCK]: [
      DISCLAIMER_KEYS.STOCK_NO_DATA,
      DISCLAIMER_KEYS.STOCK_CLOSED,
      DISCLAIMER_KEYS.STOCK_LESS_THAN_3Y,
      DISCLAIMER_KEYS.STOCK_LESS_THAN_1Y,
    ],
    [COMPARISON_ITEM_TYPES.PORTFOLIO]: [
      DISCLAIMER_KEYS.PORTFOLIO_CLOSED_HOLDINGS,
      DISCLAIMER_KEYS.PORTFOLIO_LESS_THAN_1Y,
      DISCLAIMER_KEYS.PORTFOLIO_LESS_THAN_3Y,
      DISCLAIMER_KEYS.PORTFOLIO_MISSING_BENCHMARK,
    ],
    [COMPARISON_ITEM_TYPES.BENCHMARK]: [
      DISCLAIMER_KEYS.BENCHMARK_NO_DATA,
      DISCLAIMER_KEYS.BENCHMARK_CLOSED,
      DISCLAIMER_KEYS.BENCHMARK_LESS_THAN_3Y,
      DISCLAIMER_KEYS.BENCHMARK_LESS_THAN_1Y,
    ],
    [COMPARISON_ITEM_TYPES.GROUP]: [
      DISCLAIMER_KEYS.GROUP_CLOSED_HOLDINGS,
      DISCLAIMER_KEYS.GROUP_LESS_THAN_1Y,
      DISCLAIMER_KEYS.GROUP_LESS_THAN_3Y,
      DISCLAIMER_KEYS.GROUP_MISSING_BENCHMARK,
    ],
    [COMPARISON_ITEM_TYPES.ALT]: [],
  };

  static defaultProps = {
    showMenu: true,
    isFXSubItem: false,
    isExpandSubItem: false,
    isEdited: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      showCustomDisclaimerModal: false,
    };
  }

  componentDidMount() {
    const { item } = this.props;
    const commentSection = document.getElementById(`comment-section-${item._id}`);

    // Fixes issue when combining two items with comments and then cancelling
    if (item.markup && commentSection) {
      this.forceUpdate();
    }
  }

  getCardHeaderDisclaimer = () => {
    const { item } = this.props;
    const { disclaimers } = item;

    if (!disclaimers) {
      return null;
    }

    const disclaimerKey = this.HEADER_DISCLAIMERS[item.type].find(key => !!disclaimers[key]);
    if (!disclaimerKey) {
      return null;
    }

    return <DisclaimerTooltip showLabel disclaimer={disclaimers[disclaimerKey]} data-private data-dd-privacy="mask" tooltipPlace="right" />;
  };

  showAddToGroup = () => {
    const { comparison, item, readOnly, markupMode, editGroups } = this.props;
    const hasGroups = comparison.items.some(item => item.type === COMPARISON_ITEM_TYPES.GROUP);
    const isValidItem = VALID_GROUP_PRODUCTS.includes(item.type as COMPARISON_ITEM_TYPES);

    return editGroups && hasGroups && !readOnly && !markupMode && isValidItem;
  };

  showExpand = () => {
    const { item, readOnly, allowExpandPortfolio, isFXSubItem } = this.props;
    return !readOnly && allowExpandPortfolio && !item._id.startsWith('new-') && !isFXSubItem;
  };

  showFX = () => {
    const { comparison, item, onToggleFXItem, showMenu, markupMode, isEdited } = this.props;

    return onToggleFXItem && showMenu && comparison.showBaseCurrencies && item.currency !== CURRENCY.CAD && !isBenchmark(item) && !markupMode && !isEdited;
  };

  showMenu = (item: ComparisonItem, readOnly?: boolean) => {
    const { showMenu } = this.props;

    if (!showMenu || item._id.startsWith('new-')) {
      return false;
    }

    if (isBenchmark(item)) {
      return !readOnly;
    }

    return true;
  };

  menuIsDisabled = () => {
    const { editedPortfolio, item } = this.props;

    return editedPortfolio && editedPortfolio._id === item._id;
  };

  handleSetCurrent = () => {
    const { item, updateComparisonItemCategory } = this.props;
    if (updateComparisonItemCategory) {
      updateComparisonItemCategory(item, ComparisonItemCategoryEnum.CURRENT);
    }
  };

  handleDeselectCurrent = () => {
    const { item, updateComparisonItemCategory } = this.props;
    if (updateComparisonItemCategory) {
      // Clear the category by setting it to undefined
      updateComparisonItemCategory(item, undefined);
    }
  };

  handleSetRecommended = () => {
    const { item, updateComparisonItemCategory } = this.props;
    if (updateComparisonItemCategory) {
      updateComparisonItemCategory(item, ComparisonItemCategoryEnum.RECOMMENDED);
    }
  };

  handleDeselectRecommended = () => {
    const { item, updateComparisonItemCategory } = this.props;
    if (updateComparisonItemCategory) {
      // Clear the category by setting it to undefined
      updateComparisonItemCategory(item, undefined);
    }
  };

  getComparisonCardMenu = () => {
    const { item, readOnly, user, sharingInfo, markupMode, disableSaving, hasStickyHeader, scrollbarContainerRef, editGroups, comparison } = this.props;

    if (!this.showMenu(item, readOnly)) {
      return null;
    }

    const menuIsDisabled = this.menuIsDisabled();

    // Check if another item is already set as current
    const hasOtherCurrentItem = comparison.items.some(i => i._id !== item._id && i.category === ComparisonItemCategoryEnum.CURRENT);

    // Check if this item is the current one
    const isCurrentItem = item.category === ComparisonItemCategoryEnum.CURRENT;

    const menu = (
      <ComparisonCardMenu
        user={user}
        item={item}
        onItemRemove={() => this.props.onItemRemove(item)}
        onModifyPortfolio={() => this.props.onModifyPortfolio(item)}
        onViewPortfolio={() => this.props.onViewPortfolio(item)}
        onExpandPortfolio={(expand: boolean) => this.props.onExpandPortfolioItem(item, expand)}
        onClonePortfolio={() => this.props.onClonePortfolio(item)}
        onAddCategoryBenchmark={() => this.props.addCategoryBenchmark(item)}
        onCloneBenchmark={() => this.props.onCloneBenchmark(item)}
        onSavePortfolio={toContact => this.props.onSavePortfolio(item, toContact)}
        onModifyBenchmark={() => this.props.onModifyBenchmark(item)}
        canEdit={!sharingInfo || sharingInfo.isMine}
        readOnly={readOnly}
        markupMode={markupMode}
        disableSaving={disableSaving}
        disabled={menuIsDisabled}
        scrollbarContainerRef={scrollbarContainerRef}
        hasStickyMenu={hasStickyHeader}
        canEditGroup={editGroups}
        onModifyGroup={() => this.props.onModifyGroup(item)}
        onSetCurrent={this.handleSetCurrent}
        onSetRecommended={this.handleSetRecommended}
        onDeselectCurrent={this.handleDeselectCurrent}
        onDeselectRecommended={this.handleDeselectRecommended}
        hasOtherCurrentItem={hasOtherCurrentItem}
        isCurrentItem={isCurrentItem}
      />
    );

    if (menuIsDisabled) {
      return (
        <Tooltip
          className="custom-tooltip-v2"
          icon="icon-pencil"
          title={formatMessage('Comparison.ItemMenu.LockedEditing.Title')}
          message={formatMessage('Comparison.ItemMenu.LockedEditing.Body')}
        >
          <div>{menu}</div>
        </Tooltip>
      );
    }

    return menu;
  };

  getCategoryDisclaimer = (item: ComparisonItem, category: ComparisonCategory) => {
    const { disclaimers } = item;

    if (!disclaimers || !category.disclaimers || !category.disclaimers[item.type]) {
      return null;
    }

    const categoryDisclaimers = category.disclaimers[item.type];
    if (!categoryDisclaimers || !categoryDisclaimers.length) {
      return null;
    }

    const disclaimerKey = categoryDisclaimers.find(d => !!disclaimers[d]);
    if (!disclaimerKey) {
      return null;
    }

    return <DisclaimerTooltip className="category-disclaimer" disclaimer={disclaimers[disclaimerKey]} data-private data-dd-privacy="mask" />;
  };

  getUserInfos = (user: User) => {
    const { comparison } = this.props;

    const comparisonWasShared = comparison.sharedBy && comparison.sharedBy !== user.id;

    let userInfos;

    if (comparisonWasShared) {
      userInfos = {
        imgUrl: comparison.sharedByInfos && comparison.sharedByInfos.imgUrl,
        name: comparison.sharedByInfos && comparison.sharedByInfos.name,
      };
    } else {
      userInfos = {
        imgUrl: user.imgUrl,
        name: `${user.fname} ${user.lname}`,
      };
    }

    return userInfos;
  };

  renderExpandedComment = (item: ComparisonItem, category: ComparisonCategory, field: ComparisonField, userInfos: Partial<BasicUserInfos>) => {
    const { user, updateTableHeight, resizeCommentLinesContainer, viewMarkup } = this.props;

    if (!item.markup) {
      return null;
    }

    const comment = item.markup[category.key] && item.markup[category.key][field.key] && item.markup[category.key][field.key].comment;

    const uniqueFieldId = getUniqueFieldId(item, category, field);

    if (!comment || !user) {
      return null;
    }

    return (
      <CommentExpanded
        id={uniqueFieldId}
        dotNumber={comment.dotNumber}
        comment={comment.value}
        isEditing={false}
        isPosted={comment.isPosted}
        timestamp={comment.timestamp && timeSince(comment.timestamp)}
        actionsDisabled
        item={item}
        category={category}
        field={field}
        updateTableHeight={updateTableHeight}
        resizeCommentLinesContainer={resizeCommentLinesContainer}
        userInfos={userInfos}
        viewMarkup={viewMarkup}
      />
    );
  };

  onExpandPortfolioClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    const { item } = this.props;
    this.props.onExpandPortfolioItem(item, !item.isExpanded);
  };

  onFXClick = (event: React.MouseEvent) => {
    event.stopPropagation();

    if (!this.props.onToggleFXItem) {
      return;
    }

    const { item } = this.props;
    this.props.onToggleFXItem(item, !item.isFX);
  };

  showExpandedComment = () => {
    const { item, user, viewMarkup, markupMode, previewMarkup } = this.props;
    const hasMarkupPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_MARKUP);
    const showAsFA = !hasMarkupPermission && viewMarkup;
    const showAsAM = hasMarkupPermission && markupMode && previewMarkup;

    return item.markup && (showAsFA || showAsAM);
  };

  getKeyMetricsHeight = (category: ComparisonCategory) => {
    if (category.key !== COMPARISON_CATEGORIES.KEY_METRICS) {
      return undefined;
    }

    return this.props.getAdditionalCategoryStyles(category);
  };

  getAltTruncatedClass = (name: string) => AltClassesTruncated[name as AltClass] || '';

  getHoldingsSymbols = (holdings: ComparisonHolding[]) => {
    const { item } = this.props;
    const isGroup = item.type === COMPARISON_ITEM_TYPES.GROUP;
    const isPortfolio = item.type === COMPARISON_ITEM_TYPES.PORTFOLIO;

    if (isGroup || isPortfolio) {
      return holdings.reduce((acc: Array<React.ReactElement>, holding: ComparisonHolding, i: number) => {
        const separator = i === 0 ? '' : ', ';

        if (holding.type === COMPARISON_ITEM_TYPES.ALT && holding.classification) {
          const name = formatLocalizedString(holding.classification);
          const truncatedClass = this.getAltTruncatedClass(name);
          const key = `comparison-card-symbol-${truncatedClass}-${i}`;
          acc.push(
            <Tooltip key={key} className="custom-tooltip-v2" data-dd-privacy="mask" data-private disabled={!name} message={name}>
              <span data-testid={key} className="d-flex align-items-center">
                {separator}
                {truncatedClass}
              </span>
            </Tooltip>,
          );
        }

        if (holding.type !== COMPARISON_ITEM_TYPES.ALT && holding.symbol) {
          acc.push(
            <span key={`comparison-card-symbol-${holding.symbol}-${i}`} className="align-middle">
              {separator}
              {holding.symbol}
            </span>,
          );
        }

        return acc;
      }, []);
    }

    return (
      <>
        {holdings
          .filter(symbol => !!symbol)
          .map(h => h.symbol)
          .join(', ')}
      </>
    );
  };

  getSymbols = () => {
    const { isExpandSubItem, item, readOnly, updateComparisonItemHolding } = this.props;

    if (item.type === COMPARISON_ITEM_TYPES.ALT) {
      const name = formatLocalizedString(item.classification);
      const truncatedClass = this.getAltTruncatedClass(name);
      return (
        <Tooltip className="custom-tooltip-v2 ms-2" data-dd-privacy="mask" data-private disabled={!name} message={name}>
          <span data-testid={`comparison-card-symbol-alt-${truncatedClass}`}>{truncatedClass}</span>
        </Tooltip>
      );
    }

    // Non expanded comparison items of type FUND only have one holding
    // we can look for the list of possible symbols at index 0 of the symbols array
    if (!isExpandSubItem && item.type === COMPARISON_ITEM_TYPES.FUND && item.holdings[0].symbols && item.holdings[0].symbols?.length > 1 && !readOnly) {
      return (
        <SelectInput onValueChange={s => updateComparisonItemHolding(item, item.holdings[0].id, { symbol: s })} selected={item.holdings[0].symbol}>
          {item.holdings[0].symbols.map(s => (
            <SelectOption key={s} value={s}>
              {s}
            </SelectOption>
          ))}
        </SelectInput>
      );
    }

    if (item.type === COMPARISON_ITEM_TYPES.BENCHMARK && item.holdings.length > 1) {
      return '';
    }

    return <span className="comparison-title-text">{this.getHoldingsSymbols(item.holdings)}</span>;
  };

  showCustomDisclaimer = (e: React.MouseEvent) => {
    // Prevent card selection from clicking on the disclaimer
    e.stopPropagation();

    this.setState({ showCustomDisclaimerModal: true });
  };

  hideCustomDisclaimerModal = () => {
    this.setState({ showCustomDisclaimerModal: false });
  };

  getCustomDisclaimerModalContent = () => {
    const { item } = this.props;
    const customDisclaimersForItem = getCustomDisclaimersForItem(item, [DISCLAIMER_TAG.COMPARISON]);
    const disclaimers = Array.from(new Set(customDisclaimersForItem.map(d => JSON.stringify(d)))).map(str => JSON.parse(str));

    return (
      <>
        {disclaimers.map(d => (
          <React.Fragment key={d.symbol}>
            <h3>{d.name}</h3>
            <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatLocalizedString(d.content)) }} />
          </React.Fragment>
        ))}
      </>
    );
  };

  getRiskProfilePill = () => {
    const { editedPortfolio, comparison, item, isSelected } = this.props;

    const profileToShow =
      comparison.riskProfile ||
      (isSelected && editedPortfolio ? editedPortfolio.riskProfile && editedPortfolio.riskProfile.profile : undefined) ||
      item.riskProfile;

    if (profileToShow === undefined) {
      return null;
    }

    return <ComparisonRiskProfileLabel profile={profileToShow} isComparisonRiskProfile={comparison.riskProfile !== undefined} />;
  };

  getCategoryLabel = () => {
    const { comparison, item } = this.props;

    const accountSetMembership = getAccountSetMembershipForComparisonItem(comparison, item);

    if (accountSetMembership) {
      const suffixLabel =
        accountSetMembership.kind === AccountSetRowKind.CURRENT
          ? formatMessage('Comparison.AccountSets.Row.Current')
          : formatMessage('Comparison.AccountSets.Row.Recommended');

      return (
        <PillLabel
          withTooltip
          className="comparison-card-pill"
          text={`${accountSetMembership.accountSet.name} - ${suffixLabel}`}
          data-testid="comparisonCardContent-pillLabel-accountSet"
        />
      );
    }

    return <ComparisonCategoryLabel category={item.category} />;
  };

  onRemoveItemFromGroup = (group: ComparisonItemGroup) => {
    if (this.props.onRemoveItemFromGroup) {
      this.props.onRemoveItemFromGroup(this.props.item, group);
    }
  };

  onAddItemToGroup = (group: ComparisonItemGroup, value: number, fee?: number) => {
    if (this.props.onAddItemToGroup) {
      this.props.onAddItemToGroup(this.props.item, group, value, fee);
    }
  };

  renderExpandIcon = () => {
    const { hasStickyHeader, item, user } = this.props;
    const isPortfolioExpandLimited = shouldExpandPortfolioInReadOnly(item, user);
    const tooltipMessage = formatMessage(isPortfolioExpandLimited ? 'Comparison.PortfolioExpandLimited' : `Common.${item.isExpanded ? 'Collapse' : 'Expand'}`);

    return (
      <IconButton
        className={classNames('expand-portfolio-btn icon-button-v2', { active: item.isExpanded })}
        icon="expand"
        onClick={this.onExpandPortfolioClick}
        tooltip={tooltipMessage}
        tooltipClassName={classNames('custom-tooltip-v2', { 'has-long-info': isPortfolioExpandLimited })}
        disabled={isPortfolioExpandLimited}
        tooltipPlace={!hasStickyHeader ? 'top' : 'bottom'}
      />
    );
  };

  render() {
    const {
      item,
      readOnly,
      categories,
      sharingInfo,
      user,
      markupMode,
      viewMarkup,
      markupTool,
      setMarkupComment,
      postMarkupComment,
      getNewDotNumber,
      clearMarkup,
      riskPeriod,
      onShowCorrelation,
      previewMarkup,
      comparison,
      isFXSubItem,
      disableStickyHeader,
      isExpandSubItem,
      editedBenchmark,
      popoverContainerRef,
      scrollbarContainerRef,
      hasStickyHeader,
      showGroups,
      editGroups,
      locale,
    } = this.props;

    const { showCustomDisclaimerModal } = this.state;

    if (!user) {
      return null;
    }

    const commentSection = document.getElementById(`comment-section-${item._id}`);
    const userInfos = this.getUserInfos(user);
    const showCurrency = !isFXSubItem && comparison.showBaseCurrencies && userHasPermissionEnabled(user, PERMISSIONS.CURRENCY_CONVERSIONS);
    const showPortfolioType =
      !isFXSubItem && !item.providedBy && item.portfolioType && !item._id.startsWith('new-') && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO;
    const showLongerHistory = comparison.shortHistory && item.disclaimers && item.disclaimers[DISCLAIMER_KEYS.SHORT_HOLDINGS];
    const showCustomItemDisclaimer = getCustomDisclaimersForItem(item, [DISCLAIMER_TAG.COMPARISON]).length > 0;
    const showAddToGroup = this.showAddToGroup();
    const shelfName = getShelfNameFromComparisonItem(item, user);

    const itemName = formatLocalizedString(item.name);
    const comparisonGroups = comparison.items.filter(item => item.type === COMPARISON_ITEM_TYPES.GROUP);

    return (
      <>
        <StickyContainer className="comparison-card-content" data-testid="comparison-card-content-v2">
          <Sticky disableCompensation={disableStickyHeader}>
            {({ style, isSticky }) => {
              const showEnterpriseInfo = !item.providedBy && !!item.enterprise && !isSticky;
              const showLimitedEditIcon = !item.enterprise && !sharingInfo && item.overrideHoldings?.length;
              const showLockIcon =
                !showLimitedEditIcon && item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && item.availableActions && !item.availableActions.edit;
              const showCardHeaderDisclaimer = !showLongerHistory && !isSticky;
              const sticky = isSticky && !disableStickyHeader;

              return (
                <div className={classNames('comparison-card-header', { 'is-sticky': sticky })} style={sticky ? style : undefined}>
                  <div className="header-top-row">
                    <div className="top-row-info">
                      {sharingInfo && (
                        <div className="comparison-card-sharing-info">
                          <i className="icon-user" />
                          <span>{sharingInfo.label}</span>
                        </div>
                      )}

                      {!isFXSubItem && item.holdings.length > 0 && (
                        <div className="comparison-card-title d-flex align-items-center">
                          <ComparisonCardHoldingIcon
                            item={item}
                            updateComparisonItemColor={this.props.updateComparisonItemColor}
                            popoverContainerRef={popoverContainerRef}
                            scrollbarContainerRef={scrollbarContainerRef}
                            disableColorPicker={isExpandSubItem || isSticky}
                          />
                          {this.getSymbols()}
                        </div>
                      )}

                      {isFXSubItem && (
                        <div className="comparison-card-title">
                          <HoldingIcon type={item.type} color={item.color} />

                          <span className="comparison-title-text">
                            <FormattedMessage id="Comparison.ConvertedToCAD" />
                          </span>
                        </div>
                      )}
                    </div>

                    <div className="comparison-card-actions">
                      {showAddToGroup && (
                        <ComparisonGroupsMenu
                          item={item}
                          allGroupsItems={comparisonGroups.map(group => ({
                            name: group.name,
                            itemId: group._id,
                            color: group.color,
                          }))}
                          onRemoveFromGroup={this.onRemoveItemFromGroup}
                          onAddToGroup={this.onAddItemToGroup}
                          scrollbarContainerRef={scrollbarContainerRef}
                          positionFixed={!hasStickyHeader}
                          menuTooltip={formatMessage('Comparison.Groups.Tooltips.MenuAdd')}
                          menuIcon="new-group"
                          editGroups={!readOnly}
                        />
                      )}

                      {this.showFX() && (
                        <IconButton
                          className={classNames('fx-btn', { active: item.isFX })}
                          icon="fx"
                          onClick={this.onFXClick}
                          tooltip={formatMessage('Comparison.CompareCAD')}
                          tooltipClassName="custom-tooltip-v2"
                        />
                      )}

                      {this.showExpand() && this.renderExpandIcon()}

                      {this.getComparisonCardMenu()}
                    </div>
                  </div>

                  <div data-private data-dd-privacy="mask" className="comparison-card-name">
                    {showLockIcon && <i className="icon-lock-closed" style={{ color: item.color }} />}

                    <div>
                      <Tooltip message={itemName} delayShow={300} data-private data-dd-privacy="mask">
                        <span id={`row-${item._id}`}>
                          <Dotdotdot useNativeClamp clamp={2}>
                            {itemName}
                          </Dotdotdot>
                        </span>
                      </Tooltip>

                      {item.flags && <ShelfStatus isV2 statuses={item.flags} title={shelfName} />}
                    </div>

                    <div className="comparison-card-labels">
                      {showCurrency && item.currency && <CurrencyLabel currency={item.currency} />}

                      {item.flags && item.flags.length > 0 && <CustomTypePills flaggableInstrument={item} />}
                    </div>
                  </div>

                  <div className="comparison-card-pills-container">
                    {showPortfolioType && item.portfolioType && <PortfolioTypeLabel size="extraSmall" portfolioType={item.portfolioType} />}
                    {this.getRiskProfilePill()}
                  </div>

                  {!!item.contactsInfo && <ComparisonCardContacts contacts={item.contactsInfo} />}

                  {item.flags && <ComparisonCardWarnings warnings={item.flags} />}

                  {showEnterpriseInfo && item.enterprise && (
                    <div className="comparison-card-enterprise">
                      <span className="comparison-card-icon">
                        <Tooltip
                          title={formatMessage('Models.EnterpriseLockTooltip.Title', { name: item.enterprise.name })}
                          message={formatMessage('Models.EnterpriseLockTooltip.Message')}
                        >
                          <i className="icon-lock-closed" style={{ color: item.color }} />
                        </Tooltip>
                      </span>
                      <span className="comparison-title-text">
                        <FormattedMessage id="Models.EnterpriseLockLabel" values={{ name: item.enterprise.name }} />
                      </span>
                    </div>
                  )}

                  {showLimitedEditIcon && (
                    <div className="comparison-card-limited-edits">
                      <span className="comparison-card-icon">
                        <Tooltip title={formatMessage('Models.LimitedEditingTooltip.Title')} message={formatMessage('Models.LimitedEditingTooltip.Message')}>
                          <i className="icon-lock-outline" style={{ color: item.color }} />
                        </Tooltip>
                      </span>
                      <span className="comparison-title-text">
                        <FormattedMessage id="Models.LimitedEditingTooltip.Title" />
                      </span>
                    </div>
                  )}

                  {showCardHeaderDisclaimer && this.getCardHeaderDisclaimer()}

                  {showLongerHistory && (
                    <div className="comparison-card-disclaimer">
                      <Tooltip
                        title={formatMessage('Comparison.LongHistoryTooltip.Title')}
                        message={formatMessage('Comparison.LongHistoryTooltip.Message')}
                        list={getShortHistoryDatesForItem(item)}
                        place="right"
                      >
                        <span>
                          <i className="icon-ds-info" />
                          <FormattedMessage id="Comparison.ShowingLongerHistory" />
                        </span>
                      </Tooltip>
                    </div>
                  )}

                  {showCustomItemDisclaimer && (
                    <div className="comparison-card-disclaimer custom-item-disclaimer">
                      <PrimaryLink onClick={this.showCustomDisclaimer}>
                        <i className="icon-ds-info" />
                        <FormattedMessage id="Comparison.CustomFundDisclaimer.Title" />
                      </PrimaryLink>
                    </div>
                  )}

                  {this.getCategoryLabel()}

                  {showGroups && (
                    <ComparisonItemGroups
                      item={item}
                      onRemoveGroup={this.onRemoveItemFromGroup}
                      grouped
                      scrollbarContainerRef={scrollbarContainerRef}
                      positionFixed={!hasStickyHeader}
                      editGroups={editGroups}
                    />
                  )}
                </div>
              );
            }}
          </Sticky>

          <div className="comparison-categories">
            {categories.map(category => {
              const showEditBenchmark = isBenchmark(item) && category.key === COMPARISON_CATEGORIES.KEY_METRICS;
              const categoryIncludesCurrentItem = !category.itemTypes || category.itemTypes.includes(item.type);
              const showCategoryFields = comparisonCategoryIsShown(comparison, category);

              const categoryDynamicDisclaimer = category.getDisclaimer && category.getDisclaimer(item);
              const categoryDisclaimer = this.getCategoryDisclaimer(item, category);
              const showDisclaimer = categoryIncludesCurrentItem && showCategoryFields && (categoryDisclaimer != null || categoryDynamicDisclaimer != null);

              return (
                <div className={`comparison-category ${category.key}`} style={this.getKeyMetricsHeight(category)} key={category.key}>
                  {showEditBenchmark && (
                    <ComparisonBenchmark
                      editedBenchmark={editedBenchmark}
                      user={user}
                      readOnly={readOnly}
                      item={item}
                      showCurrencies={!!comparison.showBaseCurrencies}
                    />
                  )}
                  <ComparisonBaseCell
                    className={classNames('category-header', {
                      controlled: category.controlled,
                      'has-content': showDisclaimer,
                      dynamic: category.dynamicConfig,
                    })}
                    role="cell"
                    id={`category-section-${category.key}-${item._id}`}
                  >
                    {showDisclaimer && (
                      <div className="section-disclaimer">
                        <span>
                          {categoryDynamicDisclaimer}
                          {categoryDisclaimer}
                        </span>
                      </div>
                    )}
                  </ComparisonBaseCell>

                  {showCategoryFields &&
                    category.fields.map(field => (
                      <React.Fragment key={field.key}>
                        <ComparisonValueCell
                          user={user}
                          riskPeriod={riskPeriod}
                          comparisonItem={item}
                          category={category}
                          field={field}
                          viewMarkup={viewMarkup}
                          markupTool={markupTool}
                          markupMode={markupMode}
                          onClick={markupMode ? () => this.props.onCellClick(item, category, field) : undefined}
                          setMarkupComment={setMarkupComment}
                          postMarkupComment={postMarkupComment}
                          clearMarkup={clearMarkup}
                          getNewDotNumber={getNewDotNumber}
                          userInfos={userInfos}
                          onShowCorrelation={onShowCorrelation}
                          previewMarkup={previewMarkup}
                          showBaseCurrencyValues={!!comparison.showBaseCurrencies && !isFXSubItem}
                          useLongHistory={comparison.shortHistory}
                          comparison={comparison}
                          readOnly={readOnly}
                          locale={locale}
                        />

                        {field.subFields &&
                          comparison.expandedFields?.includes(field.key) &&
                          field.subFields.map(subField => (
                            <React.Fragment key={`${field.key}-${subField.key}`}>
                              <ComparisonValueCell
                                user={user}
                                riskPeriod={riskPeriod}
                                comparisonItem={item}
                                category={category}
                                viewMarkup={viewMarkup}
                                markupTool={markupTool}
                                markupMode={markupMode}
                                onClick={markupMode ? () => this.props.onCellClick(item, category, subField) : undefined}
                                setMarkupComment={setMarkupComment}
                                postMarkupComment={postMarkupComment}
                                clearMarkup={clearMarkup}
                                getNewDotNumber={getNewDotNumber}
                                userInfos={userInfos}
                                onShowCorrelation={onShowCorrelation}
                                previewMarkup={previewMarkup}
                                showBaseCurrencyValues={!!comparison.showBaseCurrencies && !isFXSubItem}
                                useLongHistory={comparison.shortHistory}
                                comparison={comparison}
                                readOnly={readOnly}
                                locale={locale}
                                field={subField}
                              />
                            </React.Fragment>
                          ))}

                        {this.showExpandedComment() &&
                          commentSection &&
                          ReactDOM.createPortal(this.renderExpandedComment(item, category, field, userInfos), commentSection)}
                      </React.Fragment>
                    ))}
                </div>
              );
            })}
          </div>
        </StickyContainer>

        <GenericTextModal
          backdropClassName="modal-v2-backdrop"
          modalClassName="modal-v2"
          title={formatMessage('Common.Disclaimer')}
          show={showCustomDisclaimerModal}
          onCancel={this.hideCustomDisclaimerModal}
          body={this.getCustomDisclaimerModalContent()}
          isV2
        />
      </>
    );
  }
}

const ComparisonCardContent = (props: Props) => {
  const { isLightVersion } = useThemeVersion();
  if (isLightVersion) {
    return <ComparisonCardContentV2 {...props} />;
  }
  return <LegacyComparisonCardContent {...props} />;
};

export default ComparisonCardContent;
